import React, { useState, useEffect } from "react";
import { Icon } from "./icons.jsx";
import { useStore, StoreProvider } from "./store.jsx";
import { Dashboard } from "./screens/dashboard.jsx";
import { EntityProfile } from "./screens/entity.jsx";
import { Departments, Levels, Positions } from "./screens/orgchart.jsx";
import { Factors } from "./screens/factors.jsx";
import { Scales } from "./screens/scales.jsx";
import { ValuationList, ValuationDetail } from "./screens/valuation.jsx";
import { Users, Roles } from "./screens/users.jsx";
import { NotificationButton } from "./notifications.jsx";
import { Tweaks } from "./tweaks.jsx";

/* ============================================================
   Talention — App shell + simple hash router
   ============================================================ */

/* --- Hash router --- */
function useRoute() {
  const [route, setRoute] = useState(() => (window.location.hash || "#/dashboard").replace(/^#/, ""));
  useEffect(() => {
    const onHash = () => setRoute((window.location.hash || "#/dashboard").replace(/^#/, ""));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return [route, (r) => { window.location.hash = r; }];
}

/* --- Sidebar nav config --- */
const NAV = [
  {
    label: "General",
    items: [
      { id: "/dashboard", icon: "dashboard", name: "Dashboard" },
      { id: "/entity",    icon: "building",  name: "Perfil de Entidad" },
    ],
  },
  {
    label: "Organigrama",
    items: [
      { id: "/departments", icon: "users",    name: "Departamentos" },
      { id: "/levels",      icon: "tree",     name: "Niveles Jerárquicos" },
      { id: "/positions",   icon: "briefcase", name: "Puestos" },
    ],
  },
  {
    label: "Valoración de Puestos",
    items: [
      { id: "/factors", icon: "sliders",    name: "Factores" },
      { id: "/scales",  icon: "scale",      name: "Escalas" },
      { id: "/valuation", icon: "check-list", name: "Valorar Puestos" },
    ],
  },
  {
    label: "Administración",
    items: [
      { id: "/users", icon: "users", name: "Usuarios" },
      { id: "/roles", icon: "lock",  name: "Roles y permisos" },
    ],
  },
];

const PAGE_META = {
  "/dashboard":   { eyebrow: "Visión global",       title: "Panel Dashboard" },
  "/entity":      { eyebrow: "Gestión de entidad",  title: "Perfil de Entidad" },
  "/departments": { eyebrow: "Organigrama",         title: "Departamentos" },
  "/levels":      { eyebrow: "Organigrama",         title: "Niveles Jerárquicos" },
  "/positions":   { eyebrow: "Organigrama",         title: "Puestos" },
  "/factors":     { eyebrow: "Valoración",          title: "Factores de Valoración" },
  "/scales":      { eyebrow: "Valoración",          title: "Escalas Salariales" },
  "/valuation":   { eyebrow: "Valoración",          title: "Valorar Puestos" },
  "/users":       { eyebrow: "Administración",      title: "Usuarios" },
  "/roles":       { eyebrow: "Administración",      title: "Roles y permisos" },
};

function Sidebar({ route, go }) {
  const { state } = useStore();
  const totalSVPT = state.factors.groups.reduce((s, g) => s + g.svpt, 0);
  const factorsBad = totalSVPT !== 100;
  const pending = state.positions.filter(p => p.status !== "FINALIZADA").length;

  // detect /valuation/123
  const isActive = (id) => route === id || route.startsWith(id + "/");

  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-mark">T</div>
        <div>
          <div className="brand-name">Talention</div>
          <div className="brand-sub">Gestión Laboral</div>
        </div>
      </div>

      {NAV.map(group => (
        <div key={group.label}>
          <div className="nav-group-label">{group.label}</div>
          <div className="nav-list">
            {group.items.map(item => (
              <button
                key={item.id}
                className={"nav-item " + (isActive(item.id) ? "active" : "")}
                onClick={() => go(item.id)}
              >
                <span className="icon"><Icon name={item.icon} size={17} /></span>
                <span>{item.name}</span>
                {item.id === "/factors" && factorsBad && <span className="badge warn">!</span>}
                {item.id === "/valuation" && pending > 0 && <span className="badge">{pending}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="user-card">
        <div className="avatar">JM</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="user-name">José Luis M.</div>
          <div className="user-role">Administrador</div>
        </div>
        <button className="icon-btn" style={{ width: 28, height: 28 }}>
          <Icon name="settings" size={14} />
        </button>
      </div>
    </aside>
  );
}

function Topbar({ route }) {
  return (
    <header className="topbar">
      <div className="topbar-search">
        <Icon name="search" size={15} />
        <input placeholder="Buscar puestos, departamentos…" />
        <span className="kbd">⌘K</span>
      </div>
      <NotificationButton />
    </header>
  );
}

function Page({ route }) {
  // route may be /valuation/3
  if (route.startsWith("/valuation/")) {
    const id = parseInt(route.split("/")[2], 10);
    return <ValuationDetail positionId={id} />;
  }
  if (route.startsWith("/valuation")) return <ValuationList />;
  if (route.startsWith("/dashboard")) return <Dashboard />;
  if (route.startsWith("/entity")) return <EntityProfile />;
  if (route.startsWith("/departments")) return <Departments />;
  if (route.startsWith("/levels")) return <Levels />;
  if (route.startsWith("/positions")) return <Positions />;
  if (route.startsWith("/factors")) return <Factors />;
  if (route.startsWith("/scales")) return <Scales />;
  if (route.startsWith("/users")) return <Users />;
  if (route.startsWith("/roles")) return <Roles />;
  return <Dashboard />;
}

function App() {
  const [route, go] = useRoute();
  // make `go` global so children that don't have router context can navigate
  window.GO = go;
  return (
    <StoreProvider>
      <div className="app">
        <Sidebar route={route} go={go} />
        <main className="main">
          <Topbar route={route} />
          <div className="content fade-in" key={route}>
            <Page route={route} />
          </div>
        </main>
        <TweaksHost />
      </div>
    </StoreProvider>
  );
}

/* ---- Tweaks host (mounted once) ---- */
function TweaksHost() {
  return <Tweaks />;
}

export { App };
export default App;